/* eslint-disable @angular-eslint/no-output-on-prefix */
import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { Router, UrlSegment } from '@angular/router';
import { Permission, PermissionList } from '@wb/core/interfaces/account-permission';
import { AuthService } from '@wb/core/services/auth.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { GroupAccount, GroupAccountUser, GroupUser } from '@wb/core/schema/query-definitions/group.graphql';
import { DisplayService } from '@wb/core/services/display.service';
import { GroupService } from '@wb/core/services/group.service';
import { AccountService } from '../../../core/services/account.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.sass'],
})
export class HeaderComponent implements OnDestroy {
  @ViewChild('headerElement', { static: true }) headerElement?: ElementRef;

  @Input() titleBack?: string;

  @Input() title?: string;

  // fallback accountTitle for unauthorized weekly report viewers
  @Input() accountTitleFallback?: string;

  @Input() titleAction?: string;

  @Input() actionDisabled?: boolean;

  @Input() actionHidden?: boolean = false;

  @Output() onBack = new EventEmitter();

  @Output() onAction = new EventEmitter();

  public accountTitle?: string;

  public accountUid?: string;

  public isDataLoading = false;

  public isAggregator = false;

  activePermission: Permission = this.auth.defaultPermission;

  private destroyed$: Subject<void> = new Subject<void>();

  public groupAccounts: GroupAccount[] = [];

  @HostListener('window:scroll', [])
  onWindowScroll() {
    // Get the element
    const header = this.headerElement?.nativeElement;

    // If the user scrolls down past the container then affix the header
    if (window.scrollY >= 200) {
      header?.classList.add('sticky');
    } else {
      header?.classList.remove('sticky');
    }
  }

  constructor(
    private readonly accounts: AccountService,
    private readonly groups: GroupService,
    private router: Router,
    private readonly auth: AuthService,
    public readonly displayService: DisplayService,
  ) {
    this.accounts.accountLoading$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((loading) => (this.isDataLoading = loading));

    this.accounts.selectedAccount.pipe(takeUntil(this.destroyed$)).subscribe((selected) => {
      this.accountTitle = selected?.account;
      this.accountUid = selected?.uid;

      this.accounts.getAggregatorAccountEnabled.pipe(takeUntil(this.destroyed$)).subscribe((isAggregator) => {
        this.isAggregator = isAggregator ?? false;
      });

      if (selected !== undefined && selected?.group !== null) {
        const groupUid: string = selected?.group.uid || '';
        this.groups.loadGroup(groupUid).then(() => {
          this.groupAccounts = [];
          this.groups.groupAccountUsers.pipe(takeUntil(this.destroyed$)).subscribe((groupAccountUsers) => {
            const userUid = this.auth.getUserUid;
            const isExternalUser = this.auth.isExternalRole;

            groupAccountUsers.forEach((accountUsers: GroupAccountUser) => {
              if (accountUsers.account.uid === this.accountUid) return; // skip current account;

              let isAccountUser = false;

              if (isExternalUser) {
                // external users can access any account
                isAccountUser = true;
              } else {
                accountUsers.users.forEach((user: GroupUser) => {
                  if (user.uid === userUid) {
                    isAccountUser = true;
                    return;
                  }
                });
              }

              if (isAccountUser) {
                this.groupAccounts.push(accountUsers.account);
              }
            });
          });
        });
      }
    });

    this.auth.userPermission.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (response: PermissionList | undefined) => {
        if (response) {
          this.activePermission = response.account;
        }
      },
    });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  navigateToAccountHome() {
    if (this.activePermission.edit) {
      this.accounts.refetchAccount();
      this.router.navigate(['/accounts', this.accountUid]);
    }
  }

  switchAccount(account: GroupAccount) {
    const currentAccountUid = this.accountUid;
    const uidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    let nonAccountUid = 0;
    this.accounts.selectAccount(account.uid).then(async () => {
      const currentUrlTree = this.router.parseUrl(this.router.url);
      const segments = currentUrlTree.root.children.primary.segments;
      segments.forEach((urlSegment: UrlSegment) => {
        if (urlSegment.path === currentAccountUid) {
          urlSegment.path = account.uid; // Replace uid to the account we switch to
        } else {
          nonAccountUid += uidRegex.test(urlSegment.path) ? 1 : 0;
        }
      });

      if (nonAccountUid > 0) {
        // if has uid other than account uid, redirect to parent page
        this.onBack.emit();
      } else {
        const switchAccountUrl = this.router.serializeUrl(currentUrlTree);
        await this.router.navigateByUrl(switchAccountUrl);
        window.location.reload();
      }
    });
  }
}
